import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import {
    BrandingConfig,
    BrandingConfigDto,
    fromDto,
    toDto,
    getDefaultBrandingConfig
} from '../../models/branding.models';
import { environment } from '../../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class BrandingService {
    // BehaviorSubject holds branding for current session
    private readonly branding$ = new BehaviorSubject<BrandingConfig>(getDefaultBrandingConfig());
    
    // Track if branding has been loaded this session
    private loaded = false;

    // Signal for reactive access in components
    public currentBranding = signal<BrandingConfig>(getDefaultBrandingConfig());

    // Computed signals for common use cases
    public headerColor = computed(() => this.currentBranding().colors.header);
    public pageBackgroundColor = computed(() => this.currentBranding().colors.pageBackground);
    public fontColor = computed(() => this.currentBranding().colors.font);
    public buttonColor = computed(() => this.currentBranding().colors.button);

    public logoUrl = computed(() => this.currentBranding().assets.logo);
    public faviconUrl = computed(() => this.currentBranding().assets.favicon);
    public embroideryLogoUrl = computed(() => this.currentBranding().assets.embroideryLogo);

    // Organization name from branding
    public organizationName = computed(() => this.currentBranding().organizationName || 'My Site');

    // Current domain slug
    public domainSlug = computed(() => this.currentBranding().organizationId || this.getDomainSlug());

    // Observable for components that prefer RxJS
    public branding = this.branding$.asObservable();

    constructor(private http: HttpClient, private titleService: Title) {
        // Set page title when organization name changes
        this.organizationName;
    }

    /**
     * Get current domain from window location
     * Supports ?domain= query param override for testing
     */
    private getDomain(): string {
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            const overrideDomain = urlParams.get('domain');
            if (overrideDomain) {
                return overrideDomain;
            }
            return window.location.hostname + (window.location.port ? `:${window.location.port}` : '');
        }
        return 'localhost:4200';
    }

    /**
     * Convert domain to URL-safe slug for API calls
     * e.g., "localhost:4200" -> "localhost4200"
     */
    private getDomainSlug(): string {
        const domain = this.getDomain();
        
        return domain
            .toLowerCase()
            .replace(/^https?:\/\//, '')
            .replace(/^www\./, '')
            .replace(':', '')
            .replace(/[/?#].*$/, '')
            .replace(/[.\s_]+/g, '')
            .replace(/[^a-z0-9]/g, '')
            || 'default';
    }

    /**
     * Load branding configuration based on current domain (PUBLIC endpoint)
     * Used for general site users who may not be authenticated yet.
     * Only fetches from API once per session, then returns cached value.
     * 
     * Endpoint: GET /api/branding/{domainSlug}
     */
    loadBranding(): Observable<BrandingConfig> {
        // If already loaded this session, return cached value
        if (this.loaded) {
            return of(this.branding$.getValue());
        }

        const domainSlug = this.getDomainSlug();
        const apiUrl = `${environment.apiUrl || ''}/api/branding/${domainSlug}`;

        console.log(`[BrandingService] Loading public branding for domain: ${domainSlug}`);

        return this.http.get<BrandingConfigDto>(apiUrl).pipe(
            tap(dto => console.log(`[BrandingService] Raw API response:`, dto)),
            map(dto => {
                const config = fromDto(dto);
                config.organizationId = domainSlug;
                return config;
            }),
            tap(config => {
                this.loaded = true;
                this.branding$.next(config);
                this.currentBranding.set(config);
                this.applyBrandingToDOM(config);
                this.updatePageTitle();
                console.log(`[BrandingService] Public branding loaded successfully for ${domainSlug}`);
            }),
            catchError(error => {
                console.warn(`[BrandingService] Failed to load public branding for ${domainSlug}, using defaults:`, error);
                const defaultConfig = getDefaultBrandingConfig();
                defaultConfig.organizationId = domainSlug;
                this.loaded = true;
                this.branding$.next(defaultConfig);
                this.currentBranding.set(defaultConfig);
                this.applyBrandingToDOM(defaultConfig);
                this.updatePageTitle();
                return of(defaultConfig);
            })
        );
    }

    /**
     * Load branding configuration for the signed-in user's organization (ADMIN endpoint)
     * Used by branding editor - returns branding for the authenticated user's org.
     * Always fetches fresh data (does not use session cache).
     * 
     * Endpoint: GET /api/branding
     */
    loadAdminBranding(): Observable<BrandingConfig> {
        const apiUrl = `${environment.apiUrl || ''}/api/branding`;

        console.log(`[BrandingService] Loading admin branding for signed-in user`);

        return this.http.get<BrandingConfigDto>(apiUrl).pipe(
            tap(dto => console.log(`[BrandingService] Raw admin API response:`, dto)),
            map(dto => fromDto(dto)),
            tap(config => {
                // If organization name is not set in branding, it will be populated later via setOrganizationName
                this.branding$.next(config);
                this.currentBranding.set(config);
                this.applyBrandingToDOM(config);
                this.updatePageTitle();
                console.log(`[BrandingService] Admin branding loaded successfully`);
            }),
            catchError(error => {
                console.warn(`[BrandingService] Failed to load admin branding, using defaults:`, error);
                const defaultConfig = getDefaultBrandingConfig();
                this.updatePageTitle();
                return of(defaultConfig);
            })
        );
    }

    /**
     * Force reload branding from API (bypasses session cache)
     */
    reloadBranding(): Observable<BrandingConfig> {
        this.loaded = false;
        return this.loadBranding();
    }

    /**
     * Save branding configuration to API
     * Saves to the current domain's branding
     */
    saveBranding(config: BrandingConfig): Observable<void> {
        const dto = toDto(config);
        const apiUrl = `${environment.apiUrl || ''}/api/branding`;

        // Ensure organizationId is set to current domain
        if (!config.organizationId) {
            config.organizationId = this.getDomainSlug();
        }

        return this.http.put(apiUrl, dto).pipe(
            tap(() => {
                this.branding$.next(config);
                this.currentBranding.set(config);
                this.applyBrandingToDOM(config);
                this.updatePageTitle();
                console.log(`[BrandingService] Branding saved for ${config.organizationId}`);
            }),
            map(() => void 0),
            catchError(error => {
                // Still update local state so user sees their changes
                this.branding$.next(config);
                this.currentBranding.set(config);
                this.applyBrandingToDOM(config);
                this.updatePageTitle();
                throw error;
            })
        );
    }

    /**
     * Apply branding colors to DOM via CSS custom properties
     */
    applyBrandingToDOM(config: BrandingConfig): void {
        if (typeof document === 'undefined') return;

        const root = document.documentElement;
        
        // Brand-prefixed variables (used by top-nav and other components)
        root.style.setProperty('--brand-header', config.colors.header);
        root.style.setProperty('--brand-font', config.colors.font);
        root.style.setProperty('--brand-button', config.colors.button);
        root.style.setProperty('--brand-background', config.colors.pageBackground);
        root.style.setProperty('--brand-primary', config.colors.header);
        root.style.setProperty('--brand-button-text', this.getContrastColor(config.colors.button));
        
        // Primary branding variables (used by styles.scss)
        root.style.setProperty('--color-primary', config.colors.header);
        root.style.setProperty('--color-secondary', config.colors.header);
        root.style.setProperty('--color-background', config.colors.pageBackground);
        root.style.setProperty('--color-accent', config.colors.button);
        root.style.setProperty('--color-text', config.colors.font);
        
        // Named branding variables
        root.style.setProperty('--header-color', config.colors.header);
        root.style.setProperty('--page-background', config.colors.pageBackground);
        root.style.setProperty('--font-color', config.colors.font);
        root.style.setProperty('--button-color', config.colors.button);
        
        // Legacy property names for backward compatibility
        root.style.setProperty('--primary-color', config.colors.header);
        root.style.setProperty('--secondary-color', config.colors.header);
        root.style.setProperty('--background-color', config.colors.pageBackground);
        root.style.setProperty('--accent-color', config.colors.button);
        root.style.setProperty('--text-color', config.colors.font);

        // Apply page background to body
        document.body.style.backgroundColor = config.colors.pageBackground;

        // Update favicon if provided
        if (config.assets.favicon) {
            this.updateFavicon(config.assets.favicon);
        }
        
        console.log('[BrandingService] Applied branding to DOM:', {
            header: config.colors.header,
            pageBackground: config.colors.pageBackground,
            font: config.colors.font,
            button: config.colors.button
        });
    }
    
    /**
     * Get contrasting text color (black or white) for a background color
     */
    private getContrastColor(hexColor: string): string {
        // Remove # if present
        const hex = hexColor.replace('#', '');
        
        // Parse RGB values
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        
        // Calculate luminance
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        
        // Return black or white based on luminance
        return luminance > 0.5 ? '#000000' : '#ffffff';
    }

    /**
     * Update the page favicon
     */
    private updateFavicon(url: string): void {
        if (typeof document === 'undefined') return;
        
        let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        link.href = url;
    }

    /**
     * Set the organization name in branding (populate from user's organization)
     * Only sets if not already set in the branding config
     */
    setOrganizationName(organizationName: string): void {
        const currentConfig = this.currentBranding();
        // Only set if not already populated
        if (!currentConfig.organizationName || currentConfig.organizationName === 'My Site') {
            currentConfig.organizationName = organizationName;
            this.currentBranding.set({ ...currentConfig });
            this.branding$.next(currentConfig);
            this.updatePageTitle();
            console.log(`[BrandingService] Organization name set to: ${organizationName}`);
        }
    }

    /**
     * Update the page title with the organization name
     */
    private updatePageTitle(): void {
        const orgName = this.organizationName();
        if (orgName && orgName !== 'No Organization') {
            this.titleService.setTitle(orgName);
        }
    }

    /**
     * Get default configuration
     */
    getDefaultConfig(): BrandingConfig {
        return getDefaultBrandingConfig();
    }
}

